/**
 * ConfigContext - System Configuration Context Provider
 * 
 * This context provides system-wide configuration values fetched from the database.
 * All components should use this context instead of hard-coded values.
 * 
 * Usage:
 *   import { useConfig } from '../context/ConfigContext';
 *   const { config, getStrategicTarget, getWorkforceTarget } = useConfig();
 *   const targetADC = getStrategicTarget('target_adc', 450);
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default configuration values - matches backend defaults
const DEFAULT_CONFIG = {
  strategic: {
    baseline_adc: 170,
    target_adc: 450,
    plan_start_year: 2026,
    plan_end_year: 2030,
    weekly_admissions_target: 20,
    discharge_rate_target: 0.06,
    alos_target: 75,
    referral_conversion_target: 0.80
  },
  workforce: {
    rncm_caseload_target: 15,
    rncm_caseload_warning: 15,
    rncm_caseload_critical: 18,
    rncm_caseload_emergency: 20,
    staff_utilization_target: 0.85,
    turnover_rate_target: 0.10,
    burnout_rate_target: 0.05,
    prn_utilization_target: 0.10,
    prn_utilization_warning: 0.12,
    optimal_visits_per_week: 18,
    rncm_final_target: 35
  },
  clinical: {
    pain_control_target: 0.95,
    pain_control_warning: 0.90,
    hospitalization_target: 0.03,
    hospitalization_warning: 0.05,
    cahps_target: 93,
    compliance_target: 0.97,
    hvldl_2_days_target: 0.92,
    hvldl_3_days_target: 0.96,
    live_discharge_target: 0.03,
    missed_visits_target: 0.05
  },
  financial: {
    monthly_revenue_target: 200000,
    cost_per_episode_target: 21000,
    net_margin_target: 0.12,
    bad_debt_target: 0.02,
    ar_90_days_warning: 0.10
  },
  validation: {
    pain_controlled_48h_percent: { target: 95, min: 70, max: 100, label: 'Pain Control 48h' },
    dyspnea_improvement_48h_percent: { target: 90, min: 70, max: 100, label: 'Dyspnea Improvement 48h' },
    anxiety_improvement_percent: { target: 85, min: 60, max: 100, label: 'Anxiety Improvement' },
    hvldl_2_days_percent: { target: 92, min: 70, max: 100, label: 'HVLDL 2 Days' },
    hvldl_3_days_percent: { target: 96, min: 80, max: 100, label: 'HVLDL 3 Days' },
    current_census: { target: 178, min: 50, max: 500, label: 'Current Census' },
    adc: { target: 178, min: 50, max: 500, label: 'Average Daily Census' },
    alos: { target: 72, min: 30, max: 180, label: 'Average Length of Stay' },
    admissions: { target: 20, min: 5, max: 50, label: 'Weekly Admissions' },
    falls_total: { target: 0, min: 0, max: 10, label: 'Total Falls' },
    falls_with_injury: { target: 0, min: 0, max: 5, label: 'Falls with Injury' },
    medication_errors_total: { target: 0, min: 0, max: 5, label: 'Medication Errors' },
    rn_turnover_rate_percent: { target: 15, min: 0, max: 50, label: 'RN Turnover Rate' },
    overtime_hours: { target: 40, min: 0, max: 100, label: 'Overtime Hours' },
    prn_utilization: { target: 10, min: 0, max: 30, label: 'PRN Utilization' },
    conversion_ratio: { target: 70, min: 40, max: 100, label: 'Conversion Ratio' },
    weekly_discharges: { target: 15, min: 0, max: 50, label: 'Weekly Discharges' },
    median_length_of_stay: { target: 65, min: 20, max: 180, label: 'Median Length of Stay' },
    net_census_change: { target: 5, min: -20, max: 30, label: 'Net Census Change' }
  },
  phases: {
    phase_1: { name: "Foundation & Stabilization", adc_target: 170, year: 1, calendar_year: 2026 },
    phase_2: { name: "Controlled Growth", adc_target: 250, year: 2, calendar_year: 2027 },
    phase_3: { name: "Regional Expansion", adc_target: 350, year: 3, calendar_year: 2028 },
    phase_4: { name: "Market Leadership", adc_target: 400, year: 4, calendar_year: 2029 },
    phase_5: { name: "Excellence & Sustainability", adc_target: 450, year: 5, calendar_year: 2030 }
  },
  alerts: {
    critical_threshold: 60,
    warning_threshold: 80,
    email_notifications_enabled: true,
    check_frequency_hours: 24
  }
};

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConfig(DEFAULT_CONFIG);
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/system-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Merge fetched config with defaults
        const mergedConfig = {
          strategic: { ...DEFAULT_CONFIG.strategic, ...(data.strategic || {}) },
          workforce: { ...DEFAULT_CONFIG.workforce, ...(data.workforce || {}) },
          clinical: { ...DEFAULT_CONFIG.clinical, ...(data.clinical || {}) },
          financial: { ...DEFAULT_CONFIG.financial, ...(data.financial || {}) },
          validation: { ...DEFAULT_CONFIG.validation, ...(data.validation || {}) },
          phases: { ...DEFAULT_CONFIG.phases, ...(data.phases || {}) },
          alerts: { ...DEFAULT_CONFIG.alerts, ...(data.alerts || {}) }
        };
        setConfig(mergedConfig);
        setLastFetched(new Date());
        setError(null);
      } else {
        // Use defaults if fetch fails
        setConfig(DEFAULT_CONFIG);
      }
    } catch (err) {
      console.warn('Failed to fetch system config, using defaults:', err);
      setConfig(DEFAULT_CONFIG);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Refresh config periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(fetchConfig, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  // Helper functions to get specific config values with fallbacks
  const getStrategicTarget = useCallback((key, defaultValue) => {
    return config.strategic?.[key] ?? defaultValue;
  }, [config.strategic]);

  const getWorkforceTarget = useCallback((key, defaultValue) => {
    return config.workforce?.[key] ?? defaultValue;
  }, [config.workforce]);

  const getClinicalTarget = useCallback((key, defaultValue) => {
    return config.clinical?.[key] ?? defaultValue;
  }, [config.clinical]);

  const getFinancialTarget = useCallback((key, defaultValue) => {
    return config.financial?.[key] ?? defaultValue;
  }, [config.financial]);

  const getPhaseTarget = useCallback((phaseId, key, defaultValue) => {
    return config.phases?.[phaseId]?.[key] ?? defaultValue;
  }, [config.phases]);

  const getAlertThreshold = useCallback((key, defaultValue) => {
    return config.alerts?.[key] ?? defaultValue;
  }, [config.alerts]);

  // Helper function to get validation target for a field
  const getValidationTarget = useCallback((fieldName) => {
    return config.validation?.[fieldName] ?? null;
  }, [config.validation]);

  // Get all validation targets
  const getValidationTargets = useCallback(() => {
    return config.validation ?? {};
  }, [config.validation]);

  // Commonly used convenience functions
  const getRNCMCaseloadTarget = useCallback(() => {
    return config.workforce?.rncm_caseload_target ?? 15;
  }, [config.workforce]);

  const getTargetADC = useCallback(() => {
    return config.strategic?.target_adc ?? 450;
  }, [config.strategic]);

  const getBaselineADC = useCallback(() => {
    return config.strategic?.baseline_adc ?? 170;
  }, [config.strategic]);

  const getPlanYears = useCallback(() => {
    return {
      start: config.strategic?.plan_start_year ?? 2026,
      end: config.strategic?.plan_end_year ?? 2030
    };
  }, [config.strategic]);

  const value = {
    config,
    loading,
    error,
    lastFetched,
    refreshConfig: fetchConfig,
    getStrategicTarget,
    getWorkforceTarget,
    getClinicalTarget,
    getFinancialTarget,
    getPhaseTarget,
    getAlertThreshold,
    getValidationTarget,
    getValidationTargets,
    // Convenience functions
    getRNCMCaseloadTarget,
    getTargetADC,
    getBaselineADC,
    getPlanYears,
    // Export defaults for reference
    DEFAULT_CONFIG
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
